/**
 * DRM Management — sig-control plane data layer (LIVE + VOD stream catalog,
 * provisioning, rotation, usage).
 *
 * sig-control is a multi-tenant Go service the portal calls DIRECTLY (controlApi,
 * JWT bearer) — exactly like healthcheckApi. Tenant is IMPLICIT: resolved
 * server-side from the JWT org claim (= ownership). So there is NO tenant /
 * serviceId in any path, and NO @mgmt /aes/control proxy.
 *
 * Stream identity is the tenant-defined broadcast address `<app>/<name>` (like
 * an RTMP app + stream key), NOT `tenant/stream`. `id` = `app + "/" + name`.
 *
 * Base controlApi already includes /api/v1 (e.g. https://sig.control.vnetwork.dev/api/v1):
 *   GET    /streams                        → list the caller's OWNED streams
 *   GET    /streams/{app}/{name}           → detail (404 if not owned)
 *   PUT    /streams/{app}/{name}           → update (owner-gated)
 *   DELETE /streams/{app}/{name}           → delete (owner-gated)
 *   POST   /streams/{app}/{name}/stop      → stop a live stream (owner-gated)
 *   POST   /provision                      → probe + register + assign shard;
 *                                            409 if `<app>/<name>` already taken
 *   POST   /provision/probe                → dry-run probe (ABR ladder)
 *   GET    /usage/summary?from&to          → owned per-stream usage rollup
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { controlApi } from '@/api/client';

// ---- Types ------------------------------------------------------------------

export type StreamKind = 'live' | 'vod';
export type StreamStatus = 'pending' | 'provisioning' | 'live' | 'stopped';

/** Stream record from sig-control. id = `app + "/" + name`. No tenant field. */
export interface DrmStream {
  id: string; // `${app}/${name}`
  app: string; // tenant-chosen broadcast app (e.g. "vtv")
  name: string; // tenant-chosen stream name / key (e.g. "vtv6plus")
  kind: StreamKind;
  source_url: string; // partner CLEAR origin m3u8
  output_url?: string; // encrypted HLS the player consumes (…/{app}/{name}/master.m3u8)
  dvr_seconds: number;
  period_secs: number; // LIVE key rotation period
  shard?: string; // assigned sig-packager shard
  status: StreamStatus;
  created_at: string; // ISO
  /** ABR ladder captured at provision time (probe.variants). May be absent on
   *  older records or while still provisioning → ABR column shows "—". */
  abr_ladder?: DrmVariant[];
  /** Last source-probe health, if sig-control re-probes periodically. */
  source_ok?: boolean;
}

/** One ABR rendition detected by the probe. */
export interface DrmVariant {
  bandwidth: number; // bits/s
  resolution?: string; // "1920x1080"
  codecs?: string; // "avc1.640028,mp4a.40.2"
  frame_rate?: number;
  uri: string;
}

/** Result of POST /provision/probe (dry-run) or the probe block of provision. */
export interface DrmProbeResult {
  is_master: boolean; // master playlist vs media playlist
  is_clear: boolean; // source must be CLEAR (un-encrypted) to ingest
  variants: DrmVariant[]; // the ABR ladder
  duration_secs?: number; // VOD only
  warnings?: string[];
}

/** POST /provision body — the tenant chooses the `<app>/<name>` address. */
export interface ProvisionInput {
  app: string;
  name: string;
  source_url: string;
  dvr_seconds?: number;
  period_secs?: number;
}

export interface ProvisionResult {
  stream: DrmStream;
  probe: DrmProbeResult;
  output_url: string;
}

/** Per-stream usage rollup from GET /usage/summary. */
export interface DrmUsageRow {
  stream_id: string; // `${app}/${name}`
  unique_ips: number;
  counters: {
    keys_issued: number;
    // Optional: the rollup omits counters a stream has none of yet (a freshly
    // provisioned stream reports keys_issued only).
    requests?: number;
    rejects?: number;
    upstream_errors?: number;
    [k: string]: number | undefined;
  };
}

export interface DrmUsageSummary {
  from: string;
  to: string;
  rows: DrmUsageRow[];
}

export interface DrmUsageFilters {
  from?: string; // ISO / YYYY-MM-DD
  to?: string;
}

// ---- Query keys (tenant implicit from JWT — no serviceId scope) -------------

export const drmKeys = {
  all: ['drm'] as const,
  streams: () => ['drm', 'streams'] as const,
  stream: (id: string) => ['drm', 'stream', id] as const,
  usageSummary: (q: Record<string, unknown>) => ['drm', 'usage-summary', q] as const,
};

/** Build `/streams/{app}/{name}{suffix}` with each segment encoded. */
function streamPath(app: string, name: string, suffix = ''): string {
  return `/streams/${encodeURIComponent(app)}/${encodeURIComponent(name)}${suffix}`;
}

/** Split an `<app>/<name>` id back into its segments for path building. */
function splitId(id: string): { app: string; name: string } {
  const i = id.indexOf('/');
  return i < 0 ? { app: id, name: '' } : { app: id.slice(0, i), name: id.slice(i + 1) };
}

function invalidateStreams(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: drmKeys.streams() });
}

// ---- Catalog (list + detail) ------------------------------------------------

export function useDrmStreams() {
  return useQuery<DrmStream[]>({
    queryKey: drmKeys.streams(),
    staleTime: 10_000,
    refetchInterval: 30_000, // live status keeps moving
    retry: false,
    // sig-control returns { streams: [...] } — unwrap to the array.
    queryFn: async () => (await controlApi.get<{ streams: DrmStream[] }>('/streams')).data.streams ?? [],
  });
}

export function useDrmStream(id: string | undefined) {
  return useQuery<DrmStream>({
    queryKey: drmKeys.stream(id ?? ''),
    enabled: !!id,
    refetchInterval: 15_000,
    retry: false,
    queryFn: async () => {
      const { app, name } = splitId(id!);
      return (await controlApi.get<DrmStream>(streamPath(app, name))).data;
    },
  });
}

// ---- Add-source wizard: probe (dry-run) then provision ----------------------

/** Wizard step 2 — dry-run probe of the partner's m3u8 (no registration). */
export function useProbeSource() {
  return useMutation<DrmProbeResult, Error, string>({
    mutationFn: async (sourceUrl: string) =>
      (await controlApi.post<DrmProbeResult>('/provision/probe', { source_url: sourceUrl })).data,
  });
}

/**
 * Wizard final step — register at `<app>/<name>`, assign a shard, start the
 * packager. Throws on 409 (address already in use) — the caller surfaces it.
 */
export function useProvisionStream() {
  const qc = useQueryClient();
  return useMutation<ProvisionResult, Error, ProvisionInput>({
    mutationFn: async (input) =>
      (await controlApi.post<ProvisionResult>('/provision', input)).data,
    onSuccess: () => invalidateStreams(qc),
  });
}

export function useStopStream() {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: async (id: string) => {
      const { app, name } = splitId(id);
      await controlApi.post(streamPath(app, name, '/stop'));
    },
    onSuccess: () => invalidateStreams(qc),
  });
}

// ---- Register / update (catalog without the packager wizard) ----------------

/**
 * POST /streams body — register an address WITHOUT provisioning a packager. This
 * is the lightweight whitelist path (e.g. CDN "Third Party Key Server" LIVE
 * streams): the control plane mirrors the address into the @aes authz set so the
 * gateway issues/serves keys for it, but no @livepackager ingest is scheduled.
 * `source_url` is OPTIONAL — omit it for key-server-only streams.
 */
export interface RegisterInput {
  app: string;
  name: string;
  kind?: StreamKind; // default "live"
  source_url?: string; // only when an origin exists (else key-server-only)
  dvr_seconds?: number;
  period_secs?: number;
}

/**
 * Register (whitelist) a stream at `<app>/<name>` without the provision wizard.
 * 409 if the address is already taken — the caller surfaces it.
 */
export function useRegisterStream() {
  const qc = useQueryClient();
  return useMutation<DrmStream, Error, RegisterInput>({
    mutationFn: async (input) => (await controlApi.post<DrmStream>('/streams', input)).data,
    onSuccess: () => invalidateStreams(qc),
  });
}

/** PUT /streams/{app}/{name} body — update config; the address is immutable. */
export interface UpdateInput {
  kind?: StreamKind;
  source_url?: string;
  dvr_seconds?: number;
  period_secs?: number;
}

/** Update a stream's config (owner-gated). The broadcast address cannot change. */
export function useUpdateStream() {
  const qc = useQueryClient();
  return useMutation<DrmStream, Error, { id: string; patch: UpdateInput }>({
    mutationFn: async ({ id, patch }) => {
      const { app, name } = splitId(id);
      return (await controlApi.put<DrmStream>(streamPath(app, name), patch)).data;
    },
    onSuccess: (_data, { id }) => {
      invalidateStreams(qc);
      void qc.invalidateQueries({ queryKey: drmKeys.stream(id) });
    },
  });
}

export function useDeleteStream() {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: async (id: string) => {
      const { app, name } = splitId(id);
      await controlApi.delete(streamPath(app, name));
    },
    onSuccess: () => invalidateStreams(qc),
  });
}

// ---- Usage / analytics ------------------------------------------------------

export function useDrmUsageSummary(filters: DrmUsageFilters = {}) {
  return useQuery<DrmUsageSummary>({
    queryKey: drmKeys.usageSummary(filters as Record<string, unknown>),
    staleTime: 30_000,
    refetchInterval: 60_000,
    retry: false,
    // sig-control returns { from, to, streams: [{ stream, unique_ips, counters }] }.
    // Map to the frontend shape ({ rows: [{ stream_id, ... }] }).
    queryFn: async () => {
      const d = (
        await controlApi.get<{
          from: string;
          to: string;
          streams: Array<{ stream: string; unique_ips: number; counters: DrmUsageRow['counters'] }>;
        }>('/usage/summary', { params: filters })
      ).data;
      return {
        from: d.from,
        to: d.to,
        rows: (d.streams ?? []).map((s) => ({
          stream_id: s.stream,
          unique_ips: s.unique_ips,
          counters: s.counters,
        })),
      };
    },
  });
}
